import React from 'react';
import { useIntl } from 'react-intl';
import { DashboardWidget } from './DashboardWidget';

interface ITrainingFrequency {
  clientId: number;
  clientName: string;
  trainingSessionsLast30Days: number;
  trainingSessionsLast15Days: number;
  trainingSessionsLast7Days: number;
  /** #6 ítem 5: el denominador — qué tenía agendado esa semana. */
  sessionsScheduledLast7Days: number;
  sessionsCompletedLast7Days: number;
  /** 0..100, o null si no tenía nada agendado (que no es lo mismo que 0%). */
  consistencyPercentageLast7Days: number | null;
}

interface Props {
  data: ITrainingFrequency[];
  loading: boolean;
  onClientClick?: (clientId: number) => void;
}

export const TrainingConsistencyWidget: React.FC<Props> = ({ data, loading, onClientClick }) => {
  const intl = useIntl();

  // #6 ítem 5: se ordena y se pinta por PORCENTAJE. Antes la barra normalizaba
  // el conteo contra el máximo de la lista, así que su largo dependía de los
  // otros alumnos y no significaba nada por sí solo.
  const sorted = [...data].sort(
    (a, b) => (b.consistencyPercentageLast7Days ?? -1) - (a.consistencyPercentageLast7Days ?? -1)
  );

  const getBarWidth = (pct: number | null) => pct ?? 0;

  const getColor = (pct: number | null) => {
    if (pct === null) return 'var(--ios-text-tertiary)';
    if (pct >= 80) return '#22c55e';
    if (pct >= 50) return '#4d7c0f';
    if (pct >= 1) return '#f59e0b';
    return '#ef4444';
  };

  const formatPct = (pct: number | null) => (pct === null ? '—' : `${pct}%`);

  return (
    <DashboardWidget
      title={intl.formatMessage({ id: 'dashboard.consistency' })}
      icon="pi pi-chart-bar"
      iconColor="#4d7c0f"
      loading={loading}
    >
      {data.length === 0 ? (
        <p style={{ color: 'var(--ios-text-tertiary)', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' }}>
          {intl.formatMessage({ id: 'dashboard.noFrequencyData' })}
        </p>
      ) : (
        <div className="flex flex-column gap-2">
          {sorted.slice(0, 6).map((client) => (
            <div
              key={client.clientId}
              className="flex align-items-center gap-2"
              style={{ cursor: onClientClick ? 'pointer' : 'default' }}
              onClick={() => onClientClick?.(client.clientId)}
            >
              <div
                style={{
                  width: '5rem',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  color: 'var(--ios-text)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flexShrink: 0
                }}
              >
                {client.clientName.split(' ')[0]}
              </div>
              <div
                style={{
                  flex: 1,
                  height: '1.2rem',
                  background: 'var(--ios-surface-subtle)',
                  borderRadius: 'var(--ios-radius-pill)',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${getBarWidth(client.consistencyPercentageLast7Days)}%`,
                    background: getColor(client.consistencyPercentageLast7Days),
                    borderRadius: 'var(--ios-radius-pill)',
                    transition: 'width 0.3s ease',
                    minWidth: (client.consistencyPercentageLast7Days ?? 0) > 0 ? '1rem' : 0
                  }}
                />
              </div>
              <span
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  color: getColor(client.consistencyPercentageLast7Days),
                  width: '3rem',
                  flexShrink: 0,
                  fontVariantNumeric: 'tabular-nums'
                }}
              >
                {formatPct(client.consistencyPercentageLast7Days)}
                {/* El crudo da el contexto que al porcentaje solo le falta:
                    3/14 y 1/4 son distintos aunque los dos ronden el 20%. */}
                <span style={{ fontWeight: 500, fontSize: '0.62rem', color: 'var(--ios-text-tertiary)' }}>
                  {client.sessionsCompletedLast7Days}/{client.sessionsScheduledLast7Days}
                </span>
              </span>
            </div>
          ))}
          <div style={{ fontSize: '0.68rem', color: 'var(--ios-text-tertiary)', textAlign: 'right' }}>
            {intl.formatMessage({ id: 'dashboard.consistencyLast7Days' })}
          </div>
        </div>
      )}
    </DashboardWidget>
  );
};
